import React from 'react';
import { Calendar } from 'lucide-react';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { cn } from '../../../utils';
import { formatUnbanDisplay } from '../../../utils/banUtils';

type Props = {
  title: string;
  variant: 'platform' | 'shop';
  active: boolean;
  reason?: string | null;
  banPeriod?: string | null;
  banUntil?: string | null;
  bannedAt?: string | null;
};

export default function BanStatusCard({
  title,
  variant,
  active,
  reason,
  banPeriod,
  banUntil,
  bannedAt,
}: Props) {
  const fmt = useFormatDate();
  const borderClass =
    variant === 'platform'
      ? active
        ? 'border-red-200 bg-red-50/80'
        : 'border-border/60 bg-muted/20'
      : active
        ? 'border-amber-200 bg-amber-50/80'
        : 'border-border/60 bg-muted/20';

  return (
    <div className={cn('rounded-lg border p-4 space-y-3', borderClass)}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <span
          className={cn(
            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium',
            active ? 'bg-red-500/10 text-red-800' : 'bg-muted text-muted-foreground'
          )}
        >
          {active ? 'Active' : 'None'}
        </span>
      </div>
      {active ? (
        <>
          <div className="flex justify-between gap-4 text-sm">
            <span className="text-muted-foreground shrink-0">Unban</span>
            <span className="font-semibold text-right">
              {formatUnbanDisplay(banPeriod, banUntil, fmt)}
            </span>
          </div>
          {reason ? (
            <div className="text-sm">
              <span className="text-muted-foreground">Reason: </span>
              <span className="font-medium break-words" title={reason}>
                {reason}
              </span>
            </div>
          ) : null}
          {bannedAt ? (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Banned at</span>
              <span className="flex items-center">
                <Calendar className="h-3 w-3 mr-1" aria-hidden />
                {fmt(bannedAt)}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No active restriction from this source.</p>
      )}
    </div>
  );
}
